// /proyectos/:id/observaciones — las observaciones del proyecto
// (panel postventa v2, design/claude-design/panel-postventa-v2/README.md
// §3 y §Navegación).
//
// Antes este panel vivía embebido en /proyectos/:id (DetalleProyecto),
// debajo de los KPIs y encima de la tabla de unidades. El handoff v2 lo
// separa en su propia ruta: la tabla de unidades queda en /proyectos/:id,
// las observaciones acá, y la fila de unidad navega hasta acá con
// `?unidad=<assetId>`. El filtro viaja por la URL para que el jefe pueda
// compartir «las de la 402» sin explicar el camino.
//
// Listado (handoff design/incoming/design_handoff_listado_observaciones):
// tabla compartida (`ObservacionesTable`) con un filtro por columna,
// paginación, selección persistente con barra fija y cuatro KPIs que
// funcionan como interruptores. Filtrado y paginación en el cliente
// sobre el feed (server-side = follow-up).
//
// Estado en la URL (lib/postventa/observacionesList.ts): un parámetro
// repetido por columna (`?estado=`, `?criticidad=`, `?unidad=`,
// `?partida=`, …), `?q=`, `?pagina=` y `?tam=`. Los enlaces existentes
// siguen sirviendo: `?f=` (grupo, con el alias `revisar`) se lee como
// estados; `?unidad=` y `?partida=` son el primer valor de su filtro.
// Los KPIs se calculan sobre el subconjunto por unidad y partida.
//
// El estado de cada fila sale de `presentationalState`, que lee la fase
// del engine; acá no se compara ninguna clave de estado ni el ciclo
// contra un número (fitness observation-phase-single-source).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { PostventaObservation, RecipeMeResponse } from "@bitacorapro/api-types";
import { legalObservationTargets } from "@bitacorapro/core-engine";
import { taxonomyRegistry, TAXONOMY_VERSION_CURRENT } from "@bitacorapro/postventa-taxonomy";
import { AssignBatchDrawer } from "../components/AssignBatchDrawer";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { ObservacionesFiltros } from "../components/ObservacionesFiltros";
import { ObservacionesTable, type ObsColumn } from "../components/ObservacionesTable";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Pager } from "../components/Pager";
import { ObservacionPills } from "../components/ObservacionPills";
import { SevPill } from "../components/SevPill";
import { Skel } from "../components/Skel";
import { ApiError } from "../lib/api/error";
import { useCarteraProyectoDetail, usePostventaObservations, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { useQueryView } from "../lib/loading/useQueryView";
import {
  antiguedadLabel,
  applyFilters,
  BUCKET_ESTADOS,
  buildFilas,
  cierrePrevistoFila,
  clampPage,
  CLEAR_FILTERS,
  fechaCorta,
  FILTER_KEYS,
  hasActiveFilters,
  normalizePagingParams,
  PAGE_SIZES,
  pageSlice,
  parseFiltro as parseFiltroList,
  parseListState,
  totalPages as countPages,
  writeListState,
  type EstadoFiltro,
  type FilaObs,
  type FilterKey,
  type ListPatch,
  type PageSize,
  type SituacionPlazo,
  UNIDAD_SELECCIONADA,
} from "../lib/postventa/observacionesList";
import { groupCounts, type Bucket } from "../lib/postventa/phase";
import { observationActorLane } from "../lib/util";
import { gateOf, RefreshNotices, SlowNotice } from "./queryViewKit";

/** El nombre de la especialidad de TAXONOMÍA (eje de captura que escribe
 *  la asignación): ordena la nómina del drawer de asignación en lote y
 *  entra al texto que la búsqueda recorre. Lo que se MUESTRA como especialidad es la etiqueta de la receta
 *  (`especialidadLabel` sobre `category`), la misma que ven el
 *  propietario y el contratista. El feed no manda la `taxonomyVersion`
 *  de cada fila, así que se decodifica contra el catálogo vigente. */
function especialidadNombre(code: string | null | undefined): string | null {
  if (!code) return null;
  const catalog = taxonomyRegistry.get(TAXONOMY_VERSION_CURRENT);
  return catalog?.especialidades.find((e) => e.codigo === code)?.nombre ?? code;
}

/** El nombre de una partida según el catálogo vigente: respaldo de
 *  `partidaNombre` cuando el feed no lo trae. */
function partidaNombreCatalogo(code: string): string | null {
  const catalog = taxonomyRegistry.get(TAXONOMY_VERSION_CURRENT);
  for (const e of catalog?.especialidades ?? []) {
    const p = e.partidas.find((x) => x.codigo === code);
    if (p) return p.nombre;
  }
  return null;
}

// El grupo de `?f=` (enlaces del proyecto y del sidebar). Se conserva el
// export: `?f=` se lee como estados en parseListState.
export type ObservacionesFiltro = Bucket;

export const FILTRO_DEFAULT: ObservacionesFiltro = "all";

/** Lee `?f=`. El alias `revisar` se normaliza a `pendientes` al leer y
 *  nunca se escribe de vuelta. Cualquier otro valor cae al default. */
export const parseFiltro: (raw: string | null) => ObservacionesFiltro = parseFiltroList;

/** Tope del lote de asignación (el servidor acepta hasta 50 ids). */
const MAX_LOTE = 50;

const COLUMNAS = [
  "Observación",
  "Unidad",
  "Criticidad",
  "Estado",
  "Especialidad",
  "Contratista",
  "Antigüedad",
  "Asignación / plazo",
];

const PLAZO_PILL: Record<"ok" | "info" | "warn" | "crit" | "idle", string> = {
  ok: "st-pill--ok",
  info: "st-pill--info",
  warn: "st-pill--warn",
  crit: "st-pill--crit",
  idle: "st-pill--idle",
};

/** La celda «Asignación / plazo»: pill de la situación y, si hay
 *  asignación con fecha, «Asignada {fecha} · plazo {n} días». Lo que
 *  espera al propietario lleva su fecha de cierre previsto (ADR-0142),
 *  cuando el servidor la proyecta. */
export function PlazoCell({ f }: { f: FilaObs }) {
  const { plazo, o } = f;
  const sub =
    o.assignedAt && f.state.kind === "assigned"
      ? `Asignada ${fechaCorta(o.assignedAt)}${o.plazoDias ? ` · plazo ${o.plazoDias} ${o.plazoDias === 1 ? "día" : "días"}` : ""}`
      : cierrePrevistoFila(o, f.state);
  return (
    <div className="cell-with-sub">
      {plazo.pill ? (
        <span className={`st-pill ${PLAZO_PILL[plazo.tone]}`}>{plazo.pill}</span>
      ) : (
        <span className="cell-meta">{plazo.vacio}</span>
      )}
      {sub && <span className="cell-sub">{sub}</span>}
    </div>
  );
}

/** La celda «Antigüedad»: desde el reporte original. */
export function AntiguedadCell({ f }: { f: FilaObs }) {
  return (
    <div className="cell-with-sub">
      <span className="cell-strong mono">{antiguedadLabel(f.diasDesdeReporte)}</span>
      <span className="cell-sub">desde {fechaCorta(f.o.createdAt)}</span>
    </div>
  );
}

/** La celda «Criticidad»: el pill y, si hubo aviso inmediato al jefe por
 *  observación crítica (ADR-0156), la marca debajo. Sin marca, el pill
 *  va directo en la celda, como antes. Con marca, el pill va envuelto:
 *  `.cell-with-sub` es una columna flex que estira a sus hijos, y el
 *  pill estirado tomaría el ancho de la marca (y, por la tabla, el de
 *  toda la columna). */
export function CriticidadCell({ f }: { f: FilaObs }) {
  if (!f.avisoCritico) return <SevPill code={f.sev} />;
  return (
    <div className="cell-with-sub">
      <span>
        <SevPill code={f.sev} />
      </span>
      <span className="cell-sub">{f.avisoCritico}</span>
    </div>
  );
}

/** Skeleton que imita las columnas de la tabla v2 (README §Decisiones 11). */
function TablaSkeleton() {
  return (
    <div className="panel__body--flush">
      <table className="dtable dtable--compact">
        <thead>
          <tr>
            <th scope="col" style={{ width: 44 }} />
            {COLUMNAS.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              <td />
              <td>
                <div className="skel-stack">
                  <Skel width="55%" height={12} />
                  <Skel width="35%" height={9} />
                </div>
              </td>
              <td>
                <Skel width={36} height={12} />
              </td>
              <td>
                <Skel width={88} height={20} pill />
              </td>
              <td>
                <Skel width={88} height={20} pill />
              </td>
              <td>
                <Skel width={90} height={12} />
              </td>
              <td>
                <Skel width={90} height={12} />
              </td>
              <td>
                <Skel width={60} height={12} />
              </td>
              <td>
                <Skel width={96} height={20} pill />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ObservacionesDelProyectoRoute() {
  const { id } = useParams<{ id: string }>();
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  // Sólo el encabezado necesita el proyecto; la lista viene del feed.
  const detailQuery = useCarteraProyectoDetail(id, { limit: 1 });
  // Dos queries independientes que la pantalla necesita a la vez (el
  // proyecto y la receta): el contrato las pliega en una sola vista. Un
  // `:id` ausente deja el detalle deshabilitado → «no encontrado», nunca
  // un skeleton eterno.
  const detailView = useQueryView(detailQuery);
  const recipeView = useQueryView(recipeQuery);
  const gate = gateOf([detailView, recipeView] as const);
  const retryLoad = () => {
    for (const q of [detailQuery, recipeQuery]) if (q.isPending || q.isError) void q.refetch();
  };

  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";

  const noEncontrado = (
    <div className="page-pad">
      <h1 className="page-header__title">{groupLabel} no encontrado</h1>
      <p className="page-header__sub">
        El id <code>{id}</code> no corresponde a ningún {groupLabel.toLowerCase()} de tu
        organización.
      </p>
      <Link to="/proyectos" className="btn btn--secondary">
        <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
      </Link>
    </div>
  );

  if (gate.kind === "error") {
    if (gate.error instanceof ApiError && gate.error.status === 404) return noEncontrado;
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link> / {groupLabel}
            </div>
            <h1 className="page-header__title">Observaciones</h1>
            <p className="page-header__sub">Observaciones registradas.</p>
          </div>
        </div>
        <div className="panel">
          {gate.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={gate.retry}
            />
          ) : (
            <PageError
              title={`No pudimos cargar el ${groupLabel.toLowerCase()}`}
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={gate.retry}
            />
          )}
        </div>
      </div>
    );
  }

  if (gate.kind === "loading") {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{groupPlural}</Link> / <Skel width={140} height={10} />
            </div>
            <h1 className="page-header__title">Observaciones</h1>
            <p className="page-header__sub">Observaciones registradas.</p>
          </div>
        </div>
        <PageSkeleton label="Cargando observaciones…" visible={gate.visible}>
          <div className="kpi-grid" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="kpi-tile kpi-tile--dense">
                <div className="skel-stack">
                  <Skel width="45%" height={9} />
                  <Skel width="32%" height={22} />
                  <Skel width="60%" height={9} />
                </div>
              </div>
            ))}
          </div>
          <div className="panel">
            <TablaSkeleton />
          </div>
        </PageSkeleton>
        {gate.slow && <SlowNotice onRetry={retryLoad} />}
      </div>
    );
  }

  if (gate.kind === "disabled" || !id) return noEncontrado;

  const [{ group }, readyRecipe] = gate.data;
  // El rollup del servidor manda; una vertical sin estados de activación
  // no lo emite, y entonces toda unidad cuenta como activa (no hay
  // activación que esperar antes de que alguien pueda reportar).
  const unidadesActivas = group.activeAssetCount ?? group.assetCount;

  return (
    <ObservacionesDelProyecto
      assetGroupId={id}
      recipe={readyRecipe}
      groupLabel={groupLabel}
      unidadesActivas={unidadesActivas}
      header={{
        groupPlural,
        name: group.name,
        subline: group.subline ?? null,
      }}
      // El encabezado es de la ruta: si su refresco falla, el aviso va
      // bajo el encabezado y el resto de la pantalla sigue en pie.
      headerNotice={
        <RefreshNotices
          refreshFailed={gate.refreshFailed}
          revalidatingHint={false}
          onRetry={retryLoad}
        />
      }
    />
  );
}

type KpiKey = "vencidas" | "pendientes" | "proveedor" | "conformidad";

/** Lo que aplica cada KPI: reemplaza Estado y Plazo; el resto de los
 *  filtros se conserva. */
const KPI_PRESET: Record<KpiKey, { estado: EstadoFiltro[]; plazo: SituacionPlazo[] }> = {
  vencidas: { estado: [], plazo: ["vencido"] },
  // Lo que cuenta la tile: por aceptar + por asignar + apeladas
  // (`groupCounts`). Mismo conjunto que `BUCKET_ESTADOS.pendientes`.
  pendientes: { estado: [...BUCKET_ESTADOS.pendientes], plazo: [] },
  proveedor: { estado: ["assigned"], plazo: [] },
  // ADR-0141: lo que espera al propietario — el trabajo listo por
  // revisar Y el rechazo por responder. Mismo conjunto que el bucket
  // «conformidad» de phase.ts, para que el conteo del KPI y las filas
  // que el filtro deja coincidan.
  conformidad: { estado: ["resolved", "rejected"], plazo: [] },
};

const KPI_LABEL: Record<KpiKey, string> = {
  vencidas: "Fuera de plazo",
  pendientes: "Pendientes de gestión",
  proveedor: "Con contratista",
  conformidad: "Esperan al propietario",
};

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

function resultados(n: number): string {
  return n === 0 ? "Sin resultados" : n === 1 ? "1 resultado" : `${n} resultados`;
}

/** Un único role=status para resultados, KPI, paginación y selección:
 *  debounce de 500 ms, se vacía antes de escribir y el último evento
 *  gana. */
function useAnnouncer() {
  const [msg, setMsg] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const announce = useCallback((m: string) => {
    setMsg("");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(m), 500);
  }, []);
  return [msg, announce] as const;
}

/** La pantalla de observaciones del proyecto: KPIs-interruptor, un
 *  filtro por columna, tabla paginada y selección múltiple para asignar
 *  en lote. Con `header` pinta también el encabezado de página (la ruta
 *  lo pasa; los tests del panel no). */
export function ObservacionesDelProyecto({
  assetGroupId,
  recipe,
  groupLabel,
  unidadesActivas,
  header,
  headerNotice,
}: {
  assetGroupId: string;
  recipe: RecipeMeResponse;
  groupLabel: string;
  unidadesActivas: number;
  header?: { groupPlural: string; name: string; subline: string | null };
  headerNotice?: ReactNode;
}) {
  const feedQuery = usePostventaObservations(assetGroupId);
  const { data } = feedQuery;
  // El feed es el cuerpo de la tabla: carga, error y datos salen del
  // contrato. Un refresco que falla sobre datos ya pintados los conserva
  // con un aviso, en vez de reemplazar la tabla por el error.
  const feedView = useQueryView(feedQuery);
  const feedFailed = feedView.kind === "error";
  const session = useSession();
  const lane = observationActorLane(recipe, session?.organization.role);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [asignando, setAsignando] = useState(false);
  const masterRef = useRef<HTMLInputElement>(null);
  const [live, announce] = useAnnouncer();

  // Lo que navega vive en la URL; cada clave se borra en su default.
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseListState(searchParams), [searchParams]);

  const observations = useMemo(() => data?.observations ?? [], [data]);
  // «Hoy» se fija con cada carga del feed: plazo y antigüedad se
  // recalculan al refrescar, no a cada render.
  const now = useMemo(() => new Date(), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const filas = useMemo(
    () =>
      buildFilas(observations, recipe, now, {
        especialidadTaxonomia: especialidadNombre,
        partidaNombre: partidaNombreCatalogo,
      }),
    [observations, recipe, now]
  );

  /** Asignable en lote = desde su estado, y por el carril del operador,
   *  el grafo alcanza el destino de la asignación — directo o pasando
   *  por la aceptación. Es el MISMO predicado que corre el servidor en
   *  `/findings/assign-batch`; ninguna clave literal acá. */
  const esAsignable = useMemo(() => {
    if (!lane || lane === "share_link" || lane === "asset_party") return () => false;
    const destinoDeAsignacion = (from: string | null) =>
      legalObservationTargets(recipe, from, lane).find((s) =>
        s.transitionsTo.some((t) => t.actors.includes("share_link"))
      ) ?? null;
    return (o: PostventaObservation) => {
      if (destinoDeAsignacion(o.observationStatus) !== null) return true;
      return legalObservationTargets(recipe, o.observationStatus, lane).some(
        (s) => destinoDeAsignacion(s.key) !== null
      );
    };
  }, [recipe, lane]);
  // El lote sólo toma Solicitadas (incluida la reabierta) y Aceptadas
  // (decisión 4 del fundador): una Asignada se reasigna desde su detalle
  // aunque el grafo permita asignada → asignada.
  const filaAsignable = useCallback(
    (f: FilaObs) => f.state.pendiente && esAsignable(f.o),
    [esAsignable]
  );

  // La selección persiste entre páginas y filtros; al refrescar el feed
  // se podan las que ya no existen o dejaron de ser asignables.
  useEffect(() => {
    if (!data) return;
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const validas = new Set(filas.filter(filaAsignable).map((f) => f.o.id));
      const next = new Set([...prev].filter((id) => validas.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [data, filas, filaAsignable]);

  // Base de los KPIs: el subconjunto por unidad y partida.
  const base = useMemo(
    () =>
      filas.filter(
        (f) =>
          (state.unidad.length === 0 || state.unidad.includes(f.unidadId)) &&
          (state.partida.length === 0 || state.partida.includes(f.partidaCode ?? ""))
      ),
    [filas, state.unidad, state.partida]
  );
  const counts = useMemo(
    () =>
      groupCounts(
        base.map((f) => f.o),
        recipe
      ),
    [base, recipe]
  );
  const vencidas = useMemo(
    () => base.filter((f) => f.plazo.situacion === "vencido").length,
    [base]
  );

  const filtered = useMemo(() => applyFilters(filas, state), [filas, state]);
  const pages = countPages(filtered.length, state.tam);
  const pagina = clampPage(state.pagina, filtered.length, state.tam);
  const pageRows = pageSlice(filtered, pagina, state.tam);

  function update(patch: ListPatch) {
    setSearchParams((prev) => writeListState(prev, patch), { replace: true });
  }
  /** Cambia filtros y anuncia el resultado (calculado con el estado
   *  siguiente, no con el de este render). */
  function patchAndAnnounce(patch: ListPatch, prefix = "") {
    const next = parseListState(writeListState(searchParams, patch));
    update(patch);
    announce(`${prefix}${resultados(applyFilters(filas, next).length)}`);
  }

  // Si la URL no es la canónica de lo que se pinta —página fuera de
  // rango (la tabla ya la acota), `?pagina=abc`, `?tam=999`—, se corrige
  // con replace.
  const listo = feedView.kind === "ready";
  useEffect(() => {
    if (!listo) return;
    const canon = normalizePagingParams(searchParams, pagina);
    if (canon) setSearchParams(canon, { replace: true });
  }, [listo, pagina, searchParams, setSearchParams]);

  // Etiqueta de un `?unidad=` sin observaciones (no hay fila de donde
  // sacarla): se resuelve con las unidades del proyecto, que sólo se
  // piden en ese caso. Respaldo «Unidad seleccionada»: el UUID nunca se
  // muestra.
  const unidadesSinFila = listo
    ? state.unidad.filter((u) => !filas.some((f) => f.unidadId === u))
    : [];
  const unidadesProyecto = useCarteraProyectoDetail(
    unidadesSinFila.length > 0 ? assetGroupId : undefined,
    { limit: 200 }
  );
  const unidadLabel = useCallback(
    (assetId: string): string | null => {
      const fila = filas.find((f) => f.unidadId === assetId);
      if (fila) return fila.unidad;
      const a = unidadesProyecto.data?.items.find((x) => x.id === assetId);
      return a ? (a.unit ?? a.address ?? null) : null;
    },
    [filas, unidadesProyecto.data]
  );

  const seleccion = useMemo(
    () => observations.filter((o) => selected.has(o.id)),
    [observations, selected]
  );
  /** La especialidad común de la selección, si hay una sola. Ordena la
   *  nómina del drawer; no se persiste ni se reescribe. */
  const espComun = useMemo(() => {
    const nombres = new Set(seleccion.map((o) => especialidadNombre(o.specialtyCode) ?? ""));
    nombres.delete("");
    return nombres.size === 1 ? [...nombres][0]! : null;
  }, [seleccion]);
  const unidadesSeleccionadas = new Set(seleccion.map((o) => o.assetId)).size;
  const idsPagina = new Set(pageRows.map((f) => f.o.id));
  const fuera = [...selected].filter((id) => !idsPagina.has(id)).length;

  function anunciarSeleccion(prevSize: number, nextSize: number) {
    if (nextSize === 0) announce("Selección vacía");
    else
      announce(
        `${nextSize} ${nextSize === 1 ? "seleccionada" : "seleccionadas"}${prevSize === 0 ? ". Acciones disponibles al final de la página" : ""}`
      );
  }

  function toggle(id: string) {
    const prevSize = selected.size;
    if (!selected.has(id) && prevSize >= MAX_LOTE) {
      announce(`Máximo ${MAX_LOTE} seleccionadas`);
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    anunciarSeleccion(prevSize, next.size);
    if (next.size === 0) focusMaster();
  }
  function toggleVisible(ids: string[], select: boolean) {
    const prevSize = selected.size;
    const next = new Set(selected);
    let truncada = false;
    for (const i of ids) {
      if (!select) next.delete(i);
      else if (!next.has(i)) {
        if (next.size >= MAX_LOTE) {
          truncada = true;
          break;
        }
        next.add(i);
      }
    }
    setSelected(next);
    if (truncada) announce(`Máximo ${MAX_LOTE} seleccionadas`);
    else anunciarSeleccion(prevSize, next.size);
  }
  function focusMaster() {
    setTimeout(() => {
      const m = masterRef.current;
      if (m && !m.disabled) m.focus();
      else document.querySelector<HTMLElement>("h1")?.focus();
    }, 0);
  }
  function limpiarSeleccion() {
    setSelected(new Set());
    announce("Selección vacía");
    focusMaster();
  }

  // Mientras el feed carga, los conteos de un feed vacío darían 0: el
  // número (y el desglose que depende de él) va en skeleton, con el
  // mismo retardo que la tabla. El rótulo y la pista fija se quedan.
  const feedCargando = feedView.kind === "loading";
  const kpiSkel = (width: number | string) =>
    feedView.kind === "loading" ? (
      <span data-skel-visible={String(feedView.visible)}>
        <Skel variant="text" width={width} />
      </span>
    ) : null;
  const kpiValue = (n: number) => (feedFailed ? "—" : (kpiSkel(28) ?? n));
  const kpiActivo = (k: KpiKey) =>
    sameSet(state.estado, KPI_PRESET[k].estado) && sameSet(state.plazo, KPI_PRESET[k].plazo);
  const kpiTile = (
    k: KpiKey,
    value: number,
    hint: ReactNode,
    tone: "" | "review" | "crit" = ""
  ) => {
    const activo = kpiActivo(k);
    return (
      <button
        type="button"
        className={`kpi-tile kpi-tile--dense kpi-tile--link${tone ? ` kpi-tile--${tone}` : ""}`}
        aria-pressed={activo}
        onClick={() =>
          activo
            ? patchAndAnnounce({ estado: [], plazo: [] }, "Filtro quitado. ")
            : patchAndAnnounce(KPI_PRESET[k], `Filtro: ${KPI_LABEL[k]}. `)
        }
      >
        <div className="kpi-tile__label">{KPI_LABEL[k]}</div>
        <div className="kpi-tile__value">
          <span>{kpiValue(value)}</span>
        </div>
        <div className="kpi-tile__hint">{hint}</div>
        <span className="kpi-tile__chev" aria-hidden="true">
          <Icon.ChevronRight s={18} />
        </span>
      </button>
    );
  };

  // «Partida» sólo si alguna fila la trae (no se inventan datos); si la
  // URL ya filtra por partida, el chip sigue para poder quitarla.
  const hayPartida = filas.some((f) => f.partidaCode) || state.partida.length > 0;
  const filterKeys: FilterKey[] = FILTER_KEYS.filter((k) => k !== "partida" || hayPartida);

  const columns: ObsColumn[] = [
    { key: "unidad", header: "Unidad", className: "col-num col-unit", cell: (f) => f.unidad },
    {
      key: "criticidad",
      header: "Criticidad",
      cell: (f) => <CriticidadCell f={f} />,
    },
    {
      key: "estado",
      header: "Estado",
      className: "col-estado",
      cell: (f) => (
        <span className="obs-pills">
          <ObservacionPills part="head" state={f.state} />
        </span>
      ),
    },
    {
      key: "especialidad",
      header: "Especialidad",
      className: "col-wrap",
      cell: (f) => f.especialidad ?? "—",
    },
    ...(filas.some((f) => f.partidaCode)
      ? [
          {
            key: "partida",
            header: "Partida",
            className: "col-wrap",
            cell: (f: FilaObs) => f.partida ?? "—",
          },
        ]
      : []),
    {
      key: "contratista",
      header: "Contratista",
      className: "col-wrap cell-meta",
      cell: (f) => f.contratista ?? "—",
    },
    { key: "antiguedad", header: "Antigüedad", cell: (f) => <AntiguedadCell f={f} /> },
    {
      key: "plazo",
      header: "Asignación / plazo",
      className: "col-plazo",
      cell: (f) => <PlazoCell f={f} />,
    },
  ];

  const unidadSola = state.unidad.length === 1 ? unidadLabel(state.unidad[0]!) : null;
  const filtrosActivos = hasActiveFilters(state);

  return (
    <div className="page">
      {header && (
        <div className="page-header">
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/proyectos">{header.groupPlural}</Link> /{" "}
              <Link to={`/proyectos/${assetGroupId}`}>
                {header.name}
                {header.subline ? ` — ${header.subline}` : ""}
              </Link>
              {state.unidad.length === 1 &&
                (unidadSola ? ` · Unidad ${unidadSola}` : ` · ${UNIDAD_SELECCIONADA}`)}
            </div>
            <h1 className="page-header__title" tabIndex={-1}>
              Observaciones
            </h1>
            <p className="page-header__sub">Observaciones registradas.</p>
          </div>
          <div className="page-header__actions">
            <Link to={`/proyectos/${assetGroupId}`} className="btn btn--secondary btn--sm">
              Ver unidades
            </Link>
            <Link to={`/proyectos/${assetGroupId}/archivo`} className="btn btn--secondary btn--sm">
              Ver archivo
            </Link>
          </div>
        </div>
      )}
      {headerNotice}

      <section className="kpi-grid kpi-grid--obs" aria-label="Resumen">
        {kpiTile(
          "vencidas",
          vencidas,
          "Plazo del contratista vencido",
          !feedFailed && !feedCargando && vencidas > 0 ? "crit" : ""
        )}
        {kpiTile(
          "pendientes",
          counts.pendientes,
          feedFailed
            ? "Por aceptar · por asignar"
            : (kpiSkel("70%") ??
                `${counts.porAceptar} por aceptar · ${counts.porAsignar} por asignar` +
                  (counts.apeladas > 0
                    ? ` · ${counts.apeladas} ${counts.apeladas === 1 ? "apelada" : "apeladas"}`
                    : "")),
          !feedFailed && !feedCargando && counts.pendientes > 0 ? "review" : ""
        )}
        {kpiTile("proveedor", counts.proveedor, "Asignadas, en reparación")}
        {kpiTile("conformidad", counts.conformidad, "Por revisar, o un rechazo por responder")}
      </section>

      <div className="panel" style={{ marginBottom: 20 }}>
        <ObservacionesFiltros
          filas={filas}
          state={state}
          keys={filterKeys}
          labelFallbacks={{ unidad: unidadLabel }}
          onPatch={(p) => patchAndAnnounce(p)}
          resultCount={listo ? filtered.length : null}
        />
        {feedView.kind === "ready" && (
          <RefreshNotices
            refreshFailed={feedView.refreshFailed}
            revalidatingHint={feedView.revalidatingHint}
            onRetry={() => void feedQuery.refetch()}
          />
        )}

        {feedView.kind === "loading" ? (
          <>
            {/* Sección: la pantalla ya tiene su único role=status (el
                anunciador de abajo), así que el skeleton no anuncia. */}
            <PageSkeleton
              label="Cargando observaciones…"
              visible={feedView.visible}
              announce={false}
              kind="section"
            >
              <TablaSkeleton />
            </PageSkeleton>
            {feedView.slow && <SlowNotice onRetry={() => void feedQuery.refetch()} />}
          </>
        ) : feedView.kind === "error" ? (
          <div className="panel__body--flush">
            {feedView.offline ? (
              <PageError
                title="Sin conexión"
                description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                onRetry={feedView.retry}
              />
            ) : (
              <PageError
                title="No pudimos cargar las observaciones"
                description="Revisa tu conexión e intenta de nuevo."
                onRetry={feedView.retry}
              />
            )}
          </div>
        ) : feedView.kind !== "ready" ? null : filtered.length === 0 ? (
          <div className="panel__body--flush">
            {filtrosActivos ? (
              <EmptyState
                title="Ninguna observación coincide con los filtros"
                description="Prueba con otro filtro o limpia la búsqueda."
                action={
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => patchAndAnnounce(CLEAR_FILTERS)}
                  >
                    Limpiar filtros
                  </button>
                }
              />
            ) : unidadesActivas === 0 ? (
              // Vacío «sin unidades activas» (handoff §11): sin una unidad
              // activada nadie puede reportar, así que el vacío empuja la
              // primera activación en vez de explicar la ausencia.
              <EmptyState
                title={`Este ${groupLabel.toLowerCase()} no tiene unidades activas`}
                description="Las observaciones las levantan los propietarios desde su vista. Activa una unidad —queda activa al enviar la invitación— y su propietario podrá reportar."
              />
            ) : (
              // Vacío «sin observaciones»: hay unidades activas y nadie ha
              // reportado. Sin CTA a propósito — el jefe no puede crearlas.
              <EmptyState
                title="Aún no hay observaciones"
                description="Aparecerán aquí cuando los propietarios reporten desde sus unidades."
              />
            )}
          </div>
        ) : (
          <>
            <ObservacionesTable
              wide
              filas={pageRows}
              columns={columns}
              caption={`Observaciones del ${groupLabel.toLowerCase()}`}
              rowHref={(f) => `/proyectos/${assetGroupId}/observaciones/${f.o.id}`}
              selection={{
                selected,
                isSelectable: filaAsignable,
                onToggle: toggle,
                onToggleVisible: toggleVisible,
                max: MAX_LOTE,
                masterRef,
              }}
            />
            <Pager
              numbered
              currentPage={pagina}
              totalPages={pages}
              totalItems={filtered.length}
              pageSize={state.tam}
              onPage={(n) => {
                update({ pagina: n });
                announce(`Página ${n} de ${pages}`);
              }}
              pageSizes={PAGE_SIZES}
              onPageSize={(n) => update({ tam: n as PageSize })}
            />
          </>
        )}
      </div>

      {selected.size > 0 && (
        // Barra de selección, fija al fondo del viewport mientras haya
        // algo marcado: dice cuántas van, cuántas no se ven y —lo que
        // nadie adivina— que se emite un enlace por unidad.
        <section className="selbar" aria-label="Acciones de la selección">
          <span className="selbar__n">
            {selected.size} {selected.size === 1 ? "seleccionada" : "seleccionadas"}
            {espComun ? ` · ${espComun}` : ""}
          </span>
          {fuera > 0 && <span className="selbar__sub">{fuera} en otras páginas o filtros</span>}
          <span className="selbar__sub">
            {unidadesSeleccionadas === 1
              ? "Se emite un enlace para esta unidad."
              : `Se emite un enlace por unidad: ${unidadesSeleccionadas}.`}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm selbar__clear"
            onClick={limpiarSeleccion}
          >
            Limpiar selección
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setAsignando(true)}
          >
            Asignar a contratista…
          </button>
        </section>
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {live}
      </p>

      {asignando && (
        <AssignBatchDrawer
          observations={seleccion}
          especialidadLabel={espComun}
          onClose={() => setAsignando(false)}
          onDone={() => {
            setAsignando(false);
            setSelected(new Set());
            focusMaster();
          }}
        />
      )}
    </div>
  );
}
